import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../utils';

export interface DropdownItem {
  label: string;
  value: string;
  onClick?: () => void;
  disabled?: boolean;
  divider?: boolean;
}

export interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  position?: 'bottom' | 'top' | 'left' | 'right';
  align?: 'start' | 'end';
  hover?: boolean;
  className?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  position = 'bottom',
  align = 'start',
  hover = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled && item.onClick) {
      item.onClick();
    }
    setIsOpen(false);
  };

  const dropdownClasses = cn(
    'dropdown',
    position === 'top' && 'dropdown-top',
    position === 'left' && 'dropdown-left',
    position === 'right' && 'dropdown-right',
    align === 'end' && 'dropdown-end',
    hover && 'dropdown-hover',
    isOpen && 'dropdown-open',
    className
  );

  return (
    <div ref={dropdownRef} className={dropdownClasses}>
      <div
        tabIndex={0}
        role="button"
        className="cursor-pointer"
        onClick={() => !hover && setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        {trigger}
      </div>

      <ul className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52 z-10">
        {items.map((item, index) => (
          <React.Fragment key={`${item.value}-${index}`}>
            {item.divider ? (
              <div className="divider my-1" />
            ) : (
              <li>
                <button
                  className={cn(
                    'w-full text-left',
                    item.disabled && 'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                  disabled={item.disabled}
                  onClick={() => handleItemClick(item)}
                >
                  {item.label}
                </button>
              </li>
            )}
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
};
